import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import {
  Organization,
  OrganizationRiderDetail,
  OrganizationRiderPool,
  OrganizationRiderServiceLevel,
  OrganizationServiceLevel
} from '@uzurv/clara';
import { isNullOrUndefined, PhoneNumberInputDirective, PhoneNumberUtil } from 'projects/sdk/packages/core/public_api';
import { MaterialModule } from 'projects/uzurv/src/app/material.module';
import { OrganizationRiderState } from 'projects/uzurv/src/store/organization-rider/organization-rider.reducer';
import * as OrganizationRiderActions from 'projects/uzurv/src/store/organization-rider/organization-rider.actions';
import { cloneDeep } from 'lodash';
import { RequiresPermissionsModule } from '../../directives';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActionTooltipModule } from '../../directives/action-tooltip';
import { StringValidators } from '@uzurv/core';
import { OrganizationRiderServiceLevelsModule } from '../../organization-rider-service-levels';
import { dateOfBirthValidator } from 'projects/uzurv/src/validators/date-of-birth-validator';
import { OrganizationRiderPoolUtil } from 'projects/uzurv/src/utils/organization-rider-pools.util';
export interface EditOrganizationRiderData {
  organizations: Array<Organization>;
  organization: Organization;
  organizationRiderDetail: OrganizationRiderDetail;
  organizationServiceLevels: Array<OrganizationServiceLevel>;
  organizationRiderPools: Array<OrganizationRiderPool> | null;
}

@Component({
  selector: 'app-edit-organization-rider',
  templateUrl: './edit-organization-rider-dialog.component.html',
  styleUrls: ['./edit-organization-rider-dialog.component.scss'],
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PhoneNumberInputDirective,
    RequiresPermissionsModule,
    MatTooltipModule,
    ActionTooltipModule,
    OrganizationRiderServiceLevelsModule
  ]
})
export class EditOrganizationRiderDialogComponent implements OnInit {
  editOrganizationRiderForm!: FormGroup;

  organizationRiderServiceLevels!: Array<OrganizationRiderServiceLevel>;

  organizationRiderPools!: Array<OrganizationRiderPool> | null;

  editedOrganizationRiderDetail!: OrganizationRiderDetail;
  shouldShowDeclineTooltip!: boolean;
  invalidPhoneNumber!: boolean;

  constructor(
    private dialogRef: MatDialogRef<EditOrganizationRiderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditOrganizationRiderData,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private store$: Store<State<OrganizationRiderState>>
  ) {}

  get emailTooltipDescription(): string {
    return `
      Would you like to provide an email address to:
      <br>• Receive receipt emails from UZURV
      <br>• Register for our Rider App
    `;
  }

  get shouldShowEmailDeclineSubtext(): boolean {
    return (
      this.data.organizationRiderDetail.riderEmailDeclined && !isNullOrUndefined(this.data.organizationRiderDetail.riderEmailDeclinedAt)
    );
  }

  tooltipDismissCallback = () => this.handleTooltipDismiss();

  ngOnInit(): void {
    this.editedOrganizationRiderDetail = cloneDeep(this.data.organizationRiderDetail);
    this.organizationRiderServiceLevels = cloneDeep(this.data.organizationRiderDetail.organizationRiderServiceLevels);

    // Organizations do not necessarily have rider pools. If they do, cloneDeep. If not, set null.
    this.organizationRiderPools = !isNullOrUndefined(this.data.organizationRiderPools) ? cloneDeep(this.data.organizationRiderPools) : null;

    this.shouldShowDeclineTooltip =
      isNullOrUndefined(this.data.organizationRiderDetail?.riderEmailDeclined) &&
      isNullOrUndefined(this.data.organizationRiderDetail.emailAddress);

    this.editOrganizationRiderForm = this.formBuilder.group({
      firstName: [this.editedOrganizationRiderDetail.firstName, Validators.required],
      lastName: [this.editedOrganizationRiderDetail.lastName, Validators.required],
      emailAddress: [this.editedOrganizationRiderDetail.emailAddress],
      phoneNumber: [this.editedOrganizationRiderDetail.phoneNumber, Validators.required],
      dateOfBirth: [
        this.editedOrganizationRiderDetail.dateOfBirth,
        isNullOrUndefined(this.editedOrganizationRiderDetail.dateOfBirth)
          ? dateOfBirthValidator()
          : Validators.compose([Validators.required, dateOfBirthValidator()])
      ],
      organization: [this.data.organization],
      partnerIdentifier: [this.editedOrganizationRiderDetail.partnerIdentifier, StringValidators.noSpecialCharacters()],
      organizationRiderPool: [null]
    });

    if (!isNullOrUndefined(this.editedOrganizationRiderDetail.phoneNumber)) {
      this.invalidPhoneNumber = !PhoneNumberUtil.isValidPhoneNumber(
        this.editedOrganizationRiderDetail.phoneNumber,
        PhoneNumberUtil.getCountryCode(this.editedOrganizationRiderDetail.phoneNumber)
      );
    }

    // If the organization has available rider pools...
    if (!isNullOrUndefined(this.organizationRiderPools)) {
      this.updateOrganizationRiderPoolFormField();
    }
  }

  handleOrganizationServiceLevelChange(event: OrganizationRiderServiceLevel) {
    const existingOrganizationRiderServiceLevel = this.organizationRiderServiceLevels?.find(
      orls => orls.serviceLevel?.code === event.serviceLevel?.code
    );

    if (isNullOrUndefined(existingOrganizationRiderServiceLevel)) {
      this.organizationRiderServiceLevels.push(event);
    } else {
      existingOrganizationRiderServiceLevel.enabled = event.enabled;
    }
  }

  /**
   * Handles display of the organization rider pool selection field.
   *
   * @param organizationId
   * @returns {boolean}
   */
  shouldShowOrganizationRiderPoolSelection(): boolean {
    return OrganizationRiderPoolUtil.shouldShowOrganizationRiderPoolSelection(
      this.organizationRiderPools,
      this.data.organizationRiderDetail.organizationId
    );
  }

  /**
   * Returns a string based on given organizationRiderPool ride limit information.
   *
   * @param organizationRiderPool
   * @returns {string}
   */
  getOrganizationRiderPoolDisplayText(organizationRiderPool: OrganizationRiderPool): string {
    return OrganizationRiderPoolUtil.getOrganizationRiderPoolDisplayTextViaPool(organizationRiderPool);
  }

  handleTooltipDismiss() {
    this.shouldShowDeclineTooltip = false;
    this.editedOrganizationRiderDetail.riderEmailDeclined = true;
  }

  handleSubmission() {
    this.editedOrganizationRiderDetail.firstName = this.editOrganizationRiderForm.get('firstName')?.value;
    this.editedOrganizationRiderDetail.lastName = this.editOrganizationRiderForm.get('lastName')?.value;
    this.editedOrganizationRiderDetail.emailAddress = this.editOrganizationRiderForm.get('emailAddress')?.value;
    this.editedOrganizationRiderDetail.phoneNumber = this.editOrganizationRiderForm.get('phoneNumber')?.value;
    this.editedOrganizationRiderDetail.dateOfBirth = this.editOrganizationRiderForm.get('dateOfBirth')?.value;
    this.editedOrganizationRiderDetail.partnerIdentifier = this.editOrganizationRiderForm.get('partnerIdentifier')?.value;

    this.store$.dispatch(
      OrganizationRiderActions.ModifyOrganizationRiderDetail({
        payload: {
          organizationRiderDetail: this.editedOrganizationRiderDetail,
          organizationRiderServiceLevels: isNullOrUndefined(this.organizationRiderServiceLevels)
            ? null
            : this.organizationRiderServiceLevels,
          organizationRiderPool: this.editOrganizationRiderForm.get('organizationRiderPool')?.value
        }
      })
    );
    this.dialogRef.close();
  }

  /**
   * Performs checks to ensure organization rider pool form field is in the proper state with proper validation.
   */
  private updateOrganizationRiderPoolFormField() {
    // If the organization only has one rider pool and that rider pool HAS a defined rideLimit, set that pool as selected and disable the field.
    if (this.organizationRiderPools?.length === 1 && !isNullOrUndefined(this.organizationRiderPools[0].rideLimit)) {
      this.editOrganizationRiderForm.controls.organizationRiderPool.setValue(this.organizationRiderPools[0]);
      this.editOrganizationRiderForm.controls.organizationRiderPool.disable();
    }

    // If the organization has multiple rider pools to choose from, mark the field as required.
    if (this.organizationRiderPools.length > 1) {
      // If the rider has a rider pool already assigned and there are multiple pools available...
      if (!isNullOrUndefined(this.editedOrganizationRiderDetail.organizationRiderPoolCode)) {
        // Find the pool in the array with the matching code and set it as the form value.
        this.editOrganizationRiderForm.controls.organizationRiderPool.setValue(
          this.organizationRiderPools.find(orp => orp.code === this.editedOrganizationRiderDetail.organizationRiderPoolCode)
        );
      }

      // Because the organization has at least one rider pool, mark the field as required.
      this.editOrganizationRiderForm.controls.organizationRiderPool.setValidators([Validators.required]);
    }
  }
}
